// AGM-65 Maverick TV display — IR (white-hot) imaging + full-screen crosshair.
//
// Applied as an unlit ShaderMaterial on the cockpit `_TV` plane (so it's
// self-illuminated / visible at night, like the HUD). It composites two
// offscreen renders of the seeker view:
//   • tBase — the full scene from the seeker camera, rendered normally. We
//     desaturate + tone-curve it into a dim grey "cold" base so terrain and
//     sky read dark.
//   • tHot  — enemy meshes only, drawn flat white on black. Added on top so
//     hostile vehicles glow white-hot regardless of actual scene lighting
//     (object-tagged, not a brightness trick).
// A subtle scanline + sensor grain sell the low-res sensor, and four edge-anchored
// crosshair lines with an animated centre gap (closes when in firing range) are
// drawn over the top.
//
// The renderer uses logarithmicDepthBuffer, so this in-scene material must emit
// matching fragment depth via the logdepthbuf chunks — otherwise the plane is
// depth-rejected and renders nothing.

#include <common>
#include <logdepthbuf_pars_fragment>

uniform sampler2D tBase;
uniform sampler2D tHot;
uniform vec2  uResolution;   // TV render-target size (square)
uniform float uTime;         // seconds — animates grain

// IR look
uniform float uGain;
uniform float uGamma;
uniform float uColdFloor;
uniform vec3  uHotColor;
uniform float uScanline;
uniform float uNoise;

// Crosshair
uniform vec3  uCrossColor;
uniform float uCrossHalfWidth; // line half-thickness (px)
uniform float uCrossGap;       // half-gap from centre to each line tip (px)

// Reference lines — short horizontal ticks on the vertical crosshair, between
// the centre and the bottom edge of the display.
uniform vec3  uRefColor;
uniform float uRefHalfWidth;   // tick half-thickness (px)
uniform float uRefHalfLen;     // tick half-length (px)
uniform float uRefSpan;        // px below centre where the lowest tick sits
uniform float uRefCount;       // number of evenly-spaced ticks

// Lock marker — small crosshair pinned to the tracked enemy
uniform float uLockActive;     // 0 = hidden, 1 = a target is locked + on-screen
uniform vec2  uLockUv;         // plane-space UV of the locked target (matches vUv)
uniform vec3  uLockColor;
uniform float uLockHalfWidth;  // arm half-thickness (px)
uniform float uLockSize;       // half-length of each arm (px)

varying vec2 vUv;

// Cheap hash → [0,1] for sensor grain.
float hash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

void main() {
  #include <logdepthbuf_fragment>

  // Render-target textures have a bottom-left origin, so they sample upside-down
  // on the plane — flip V to bring the feed upright.
  vec2 fuv = vec2(vUv.x, 1.0 - vUv.y);

  // ── Cold base: desaturate + tone curve, kept dim so it sits well below the
  //    white-hot targets. coldFloor keeps deep shadow from crushing to black.
  vec3 base = texture2D(tBase, fuv).rgb;
  float luma = dot(base, vec3(0.299, 0.587, 0.114));
  luma = pow(max(luma, 0.0), uGamma) * uGain;
  luma = max(luma, uColdFloor);
  vec3 col = vec3(luma);

  // ── White-hot enemies: tHot is white where a hostile is visible, else black.
  vec3 hot = texture2D(tHot, fuv).rgb;
  float hotMask = clamp(max(max(hot.r, hot.g), hot.b), 0.0, 1.0);
  col = mix(col, uHotColor, hotMask);

  // ── Sensor scanlines — gentle horizontal darkening every other row.
  if (uScanline > 0.0) {
    float scan = 0.5 + 0.5 * cos(vUv.y * uResolution.y * 3.14159265);
    col *= 1.0 - uScanline * scan;
  }

  // ── Sensor grain — animated per-pixel noise.
  if (uNoise > 0.0) {
    float n = hash(floor(vUv * uResolution) + fract(uTime) * 57.0);
    col += (n - 0.5) * uNoise;
  }

  // ── Full-screen crosshair: four lines from the edges with a centre gap.
  //    Antialiased with screen-space derivatives (fwidth) — MSAA can't smooth a
  //    shader-internal threshold, so the hard step() edges crawl without this.
  vec2 px = vUv * uResolution;
  vec2 d  = abs(px - uResolution * 0.5);
  vec2 aa = fwidth(px);
  // Vertical line: thin in x, beyond the centre gap in y. Horizontal: the transpose.
  float vLine = (1.0 - smoothstep(uCrossHalfWidth - aa.x, uCrossHalfWidth + aa.x, d.x))
              * smoothstep(uCrossGap - aa.y, uCrossGap + aa.y, d.y);
  float hLine = (1.0 - smoothstep(uCrossHalfWidth - aa.y, uCrossHalfWidth + aa.y, d.y))
              * smoothstep(uCrossGap - aa.x, uCrossGap + aa.x, d.x);
  float cross = clamp(vLine + hLine, 0.0, 1.0);
  col = mix(col, uCrossColor, cross);

  // ── Reference lines: short horizontal ticks centred on the vertical
  //    crosshair, evenly spaced between the horizontal crosshair and the visual
  //    bottom edge of the feed (vUv.y → 1 after the feed's V-flip). The lowest
  //    tick lands at uRefSpan below centre; the rest are evenly spaced above it.
  float refSpacing = uRefSpan / uRefCount;
  float refMask = 0.0;
  for (int i = 1; i <= 8; i++) {
    if (float(i) > uRefCount) break;
    float yc  = uResolution.y * 0.5 + float(i) * refSpacing;
    float dyr = abs(px.y - yc);
    float dxr = abs(px.x - uResolution.x * 0.5);
    float tick = (1.0 - smoothstep(uRefHalfWidth - aa.y, uRefHalfWidth + aa.y, dyr))
               * (1.0 - smoothstep(uRefHalfLen  - aa.x, uRefHalfLen  + aa.x, dxr));
    refMask = max(refMask, tick);
  }
  col = mix(col, uRefColor, refMask);

  // ── Lock marker: a small crosshair pinned to the tracked enemy — two solid
  //    AA'd lines crossing at the target centre (no centre gap), clamped to a
  //    short arm length so it reads as a compact marker that rides the vehicle.
  if (uLockActive > 0.5) {
    vec2 dl = abs(px - uLockUv * uResolution);
    float lvLine = (1.0 - smoothstep(uLockHalfWidth - aa.x, uLockHalfWidth + aa.x, dl.x))
                 * (1.0 - smoothstep(uLockSize - aa.y, uLockSize + aa.y, dl.y));
    float lhLine = (1.0 - smoothstep(uLockHalfWidth - aa.y, uLockHalfWidth + aa.y, dl.y))
                 * (1.0 - smoothstep(uLockSize - aa.x, uLockSize + aa.x, dl.x));
    float lock = clamp(lvLine + lhLine, 0.0, 1.0);
    col = mix(col, uLockColor, lock);
  }

  gl_FragColor = vec4(col, 1.0);
}
